/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getCompanies } from 'services/getCompanies';
import { validateUser } from 'services/validateUser';
import { getActivity } from 'services/getActivity';

import BigIcon from 'UI/atoms/BigIcon';
import CardContent from 'UI/atoms/CardContent';
import building from 'assets/img/icon/building.svg';
import Title from 'UI/atoms/Title';
import Subtitle from 'UI/atoms/Subtitle';
import TextBox from 'UI/atoms/TextBox';
import Form from 'UI/molecules/Form';
import Loader from 'UI/atoms/Loader';

import warning from 'assets/img/icon/warning.png';
import styles from './styles.module.scss';

const CompanySelection = () => {
  const [companies, setCompanies] = useState();
  const [selectedCompany, setSelectedCompany] = useState();
  const [loading, setLoading] = useState(false);
  const [processingSelection, setProcessingSelection] = useState(false);
  const [processingNoSelection, setProcessingNoSelection] = useState(false);
  const [registerValid, setRegisterValid] = useState(true);
  const { userId, code, locale, activityId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCheckbox = (company) => {
    setSelectedCompany(company.id);
  };

  useEffect(() => {
    setLoading(true);
    getCompanies(userId)
      .then((data) => {
        setCompanies(data);
        if (data.length > 0) {
          const firstCompany = data[0];
          setSelectedCompany(firstCompany.id);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const completeRegister = async (cb, companyId) => {
    const queries = [];
    queries.push(validateUser(code, companyId, true));
    if (activityId) {
      const activityParams = activityId.split('-');
      queries.push(getActivity(activityParams[0]));
    }
    Promise.all(queries).then((results) => {
      navigate(`/${locale}/register-finished`, { state: { data: results } });
    }).catch(() => {
      setRegisterValid(false);
    }).finally(() => {
      cb();
    });
  };

  const validateSelection = async () => {
    setProcessingSelection(true);
    completeRegister(() => setProcessingSelection(false), selectedCompany);
  };

  const validateNoSelection = async () => {
    setProcessingNoSelection(true);
    completeRegister(() => setProcessingNoSelection(false), null);
  };

  if (!registerValid) {
    return (<>
      <CardContent sm={{ span: 10, offset: 1 }}>
        <BigIcon src={warning} />
        <Title>{t('register.already_completed.title')}</Title>
        <Row>
          <Col xs={12} sm={{ span: 12, offset: 0 }}>
            <Subtitle>
              <span dangerouslySetInnerHTML={{ __html: t('register.already_completed.subtitle') }}></span>
            </Subtitle>
          </Col>
        </Row>
      </CardContent>
    </>);
  }

  return (
    <CardContent sm={{ span: 10, offset: 1 }}>
      <BigIcon src={building} />
      <Title>{t('company-selection.title')}</Title>
      <Row>
        <Col xs={12} sm={{ span: 10, offset: 1 }}>
          <Subtitle>
            {t('company-selection.subtitle')}
          </Subtitle>
        </Col>
      </Row>

      <form className={styles.form}>

        {loading &&
          <Row>
            <Col xs={12} sm={{ span: 10, offset: 1 }}>
              <Loader size={'big'} position={'relative'} color={'green'} />
            </Col>
          </Row>
        }

        {!loading &&
          <div className={styles.companies}>
            {companies?.map((company, index) => {
              return (
                <label key={company.id} className={styles.company}>
                  <TextBox
                    color={selectedCompany === company.id ? 'blue' : 'gray'}>
                    <div className={styles.checkBox}
                      onClick={() => handleCheckbox(company)}
                    >
                      <input
                        defaultChecked={selectedCompany === company.id}
                        type="radio"
                        name="company"
                        value={company.id}
                      ></input>
                    </div>

                    <p className={styles.companyName}>{company.social_name}</p>
                    <p className={styles.companyAddress}>
                      {company.tax_domiciles[0].address.address}, {company.tax_domiciles[0].address.city}
                      <br />
                      {t('company-selection.company.id')} {company.fiscal_id}
                    </p>
                  </TextBox>
                </label>
              );
            })}
          </div>
        }

        <div className={styles.actions}>
          <div className={styles.button}>
            <Form.Button type="button"
              disabled={processingSelection || loading}
              onClick={() => validateSelection()}>
              {t('company-selection.button.select')}
              {processingSelection && <Loader />}
            </Form.Button>
          </div>
          <div className={styles.button}>
            <Form.Button type="button"
              disabled={processingNoSelection || loading}
              style="white"
              onClick={() => validateNoSelection()}>
              {t('company-selection.button.no-selection')}
              {processingNoSelection && <Loader />}
            </Form.Button>
          </div>
        </div>
      </form>
    </CardContent>
  );
};

export default CompanySelection;
